// External Imports
import { useState, useContext, useEffect } from "react";
import { MdAdd, MdRefresh } from "react-icons/md";
import { useLocation } from "react-router-dom";

// Internal Imports
import { UtilityContext } from "../../context/util-context";
import { requestAdminAPI } from "../../api/posh-api";
import SearchBar from "../UI/SearchBar";
import Heading from "../UI/Heading";
import PaginationMenu from "../UI/PaginationMenu";
import FilterMenu from "../UI/FilterMenu";
import LoadingCard from "../UI/LoadingCard";
import NoRecordsFound from "../UI/NoRecordsFound";
import ExportCsvButton from "../UI/ExportCsvButton";
import ApplicationCard from "./ApplicationCard";
import ListLabels from "../UI/ListLabels";
import DateFilter from "../UI/DateFilter";
import { startOfDay } from "date-fns";

/** ========================================================================
 * * Applications Component
 * This component renders a list of applications by partners.
 * ========================================================================== */

export default function Applications() {
  const { navigate } = useContext(UtilityContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  // Default Filters
  const defaultFilters = {
    status: "onboarded",
    registeration: "all",
    insurance: "all",
    date: "dueDate",
    sort: "dueDate",
    installment: "all",
    query: "",
    sd: "",
    ed: "",
  };

  // Filters in URL params
  const paramFilters = {};
  Object.keys(defaultFilters).forEach(
    (key) => (paramFilters[key] = params.get(key) ?? defaultFilters[key])
  );

  // Initialize States
  const [applications, setApplications] = useState([]);
  const [limit, setLimit] = useState(50);
  const [filter, setFilter] = useState(paramFilters);
  const [loading, setLoading] = useState(true);

  /* ======================= FETCH RECORDS ======================= */
  const fetchRecords = async () => {
    try {
      setLoading(true);

      // Send Request
      let data = await requestAdminAPI("search", {
        table: "application",
        limit,
        filters: filter,
      });

      // Replace URL
      let url = "/applications?";
      Object.keys(filter).forEach((key) => {
        url += `${key}=${filter[key] ?? defaultFilters[key]}&`;
      });
      navigate(url, {}, { replace: true });

      setApplications(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // =================== FILTER OPTIONS ===================
  const filterOptions = [
    {
      label: "Status",
      options: [
        { label: "Onboarded", value: "onboarded" },
        { label: "Not Onboarded", value: "not onboarded" },
        { label: "All", value: "all" },
      ],
      selected: filter.status,
    },
    {
      label: "Date",
      options: [
        { label: "Due Date", value: "dueDate" },
        { label: "Registration Expiry", value: "registerationExp" },
        { label: "Insurance Expiry", value: "insuranceExp" },
        { label: "Date Added", value: "dateCreated" },
      ],
      selected: filter.date,
    },
    {
      label: "Registeration",
      options: [
        { label: "All", value: "all" },
        { label: "Expired", value: "expired" },
        { label: "Not Expired", value: "not expired" },
      ],
      selected: filter.registeration,
    },
    {
      label: "Insurance",
      options: [
        { label: "All", value: "all" },
        { label: "Expired", value: "expired" },
        { label: "Not Expired", value: "not expired" },
      ],
      selected: filter.insurance,
    },
    {
      label: "Installment",
      options: [
        { label: "All", value: "all" },
        { label: "Due", value: "due" },
        { label: "Paid", value: "paid" },
      ],
      selected: filter.installment,
    },
  ];

  // Calculate Total and Average
  let total = 0;
  applications.forEach((app) => {
    total += Number(app.monthlyPayment || 0);
  });

  // Fetch data on load and when filter changes
  useEffect(() => {
    fetchRecords();
  }, [filter, limit]);

  return (
    <div className="container">
      <Heading title="Applications">
        <DateFilter
          startDate={filter.sd}
          endDate={filter.ed}
          onSubmit={(sd, ed) => setFilter({ ...filter, sd, ed })}
        />
        <FilterMenu
          onSubmit={(f) => setFilter({ ...filter, ...f })}
          options={filterOptions}
        />
        <MdRefresh onClick={fetchRecords} className="icon" title="Refresh" />
        <ExportCsvButton array={applications} filename="applications" />
      </Heading>

      <SearchBar
        searchHandler={(query) => setFilter({ ...filter, query })}
        clearSearch={() => setFilter(defaultFilters)}
        searchValue={filter.query}
      />

      {loading ? (
        <LoadingCard columns={5} max={8} />
      ) : applications.length > 0 ? (
        <div className="gap-4 list">
          <div className="labels lg:grid-cols-9">
            <ListLabels
              labels={[
                { classes: "lg:col-span-2", name: "Vehicle" },
                {
                  key: "partner",
                  classes: "lg:col-span-2",
                  name: "Partner",
                },
                { key: "dueDate", name: "Next Payment" },
                { key: "lastPayment", name: "Last Payment" },
                { key: "registerationExp", name: "Registration" },
                { key: "monthlyPayment", name: "Installment" },
              ]}
              selected={filter.sort}
              setSelected={(sort) => setFilter({ ...filter, sort })}
            />
          </div>
          {applications.map((application, idx) => (
            <ApplicationCard key={idx} application={application} />
          ))}
        </div>
      ) : (
        <NoRecordsFound />
      )}

      <PaginationMenu
        limit={limit}
        setLimit={setLimit}
        loading={loading}
        count={applications.length}
      >
        <p className="text-stone-400">
          Total:{" "}
          <span className="font-semibold text-stone-200">
            ${total.toFixed(2)}
          </span>
        </p>
      </PaginationMenu>
    </div>
  );
}
